/**
 * Public Christmas offering detail for web /o/[id] (original images only).
 */

export type WebOfferingImage = {
  id: string | null;
  publicUrl: string;
  sortOrder: number;
  alt: string;
};

export type WebOfferingDetail = {
  id: string;
  restaurantId: string;
  packageTitle: string | null;
  priceEur: number | null;
  currency: string;
  pricePerPerson: boolean;
  seasonLabel: string | null;
  validFrom: string | null;
  validTo: string | null;
  derivedAvailability: string | null;
  archivedAt: string | null;
  images: WebOfferingImage[];
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function unknownString(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

function unknownNumber(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function unknownBoolean(v: unknown): boolean | null {
  if (typeof v === "boolean") return v;
  return null;
}

function httpsUrl(raw: string | null | undefined): string | null {
  const url = raw?.trim() ?? "";
  if (/^https?:\/\//i.test(url)) return url;
  return null;
}

export function parsePublicMenuOfferingForWeb(
  raw: unknown,
  titleFallback = "Menú de Navidad"
): WebOfferingDetail | null {
  let value = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  const o = asRecord(value);
  if (!o) return null;
  const id = unknownString(o.id);
  const restaurantId = unknownString(o.restaurant_id ?? o.restaurantId);
  if (!id || !restaurantId) return null;
  if (!unknownString(o.published_at ?? o.publishedAt)) return null;
  if (unknownString(o.photo_moderation_status ?? o.photoModerationStatus) === "rejected") {
    return null;
  }

  const title =
    unknownString(o.package_title ?? o.packageTitle)?.trim() || titleFallback;
  const imagesRaw = o.images;
  const images: WebOfferingImage[] = [];
  if (Array.isArray(imagesRaw)) {
    for (const item of imagesRaw) {
      const img = asRecord(item);
      if (!img) continue;
      const publicUrl = httpsUrl(unknownString(img.public_url ?? img.publicUrl));
      if (!publicUrl) continue;
      // Never expose storage_path to the UI layer.
      images.push({
        id: unknownString(img.id),
        publicUrl,
        sortOrder: unknownNumber(img.sort_order ?? img.sortOrder) ?? 0,
        alt: `${title} — ${images.length + 1}`,
      });
    }
  }
  images.sort((a, b) => a.sortOrder - b.sortOrder);
  const capped = images.slice(0, 5);

  const pricePerPerson = unknownBoolean(o.price_per_person ?? o.pricePerPerson);

  return {
    id,
    restaurantId,
    packageTitle: unknownString(o.package_title ?? o.packageTitle),
    priceEur: unknownNumber(o.price_eur ?? o.priceEur),
    currency: unknownString(o.currency) ?? "EUR",
    pricePerPerson: pricePerPerson ?? true,
    seasonLabel: unknownString(o.season_label ?? o.seasonLabel),
    validFrom: unknownString(o.valid_from ?? o.validFrom),
    validTo: unknownString(o.valid_to ?? o.validTo),
    derivedAvailability: unknownString(
      o.derived_availability ?? o.derivedAvailability
    ),
    archivedAt: unknownString(o.archived_at ?? o.archivedAt),
    images: capped,
  };
}

export function formatOfferingPrice(
  priceEur: number | null,
  currency: string
): string | null {
  if (priceEur == null || !Number.isFinite(priceEur)) return null;
  const formatted = priceEur.toFixed(2).replace(".", ",");
  if (!currency || currency.toUpperCase() === "EUR") return `${formatted} €`;
  return `${formatted} ${currency}`;
}

export function describeOfferingDates(offering: {
  validFrom: string | null;
  validTo: string | null;
}):
  | { kind: "range"; from: string; to: string }
  | { kind: "from"; from: string }
  | { kind: "until"; to: string }
  | { kind: "unknown" } {
  const from = offering.validFrom?.trim() || null;
  const to = offering.validTo?.trim() || null;
  if (from && to) return { kind: "range", from, to };
  if (from) return { kind: "from", from };
  if (to) return { kind: "until", to };
  return { kind: "unknown" };
}
